import { Box, Typography, Chip } from "@mui/material";
import CardImage from "@/components/cardImage";
import ProductImage from "@/components/productImage";

interface ProductItemProps {
  product: {
    name: string;
    image?: string;
    type?: string;
    status?: string;
  };
  isLastItem?: boolean;
}

const getStatusConfig = (status: string) => {
  const statusLower = status.toLowerCase();

  switch (statusLower) {
    case "completed":
    case "delivered":
      return {
        color: "#2e7d32",
        bgcolor: "#e8f5e9",
        label: "DELIVERED",
      };

    case "refund":
    case "refunded":
      return {
        color: "#d32f2f",
        bgcolor: "#ffebee",
        label: "REFUNDED",
      };

    case "failed":
    case "error":
      return {
        color: "#d32f2f",
        bgcolor: "#ffebee",
        label: "REFUNDED",
      };

    case "in_transit":
    case "order placed":
      return {
        color: "#1976d2",
        bgcolor: "#e3f2fd",
        label: status.replace(/_/g, " ").toUpperCase(),
      };

    case "processing":
      return {
        color: "#f57c00",
        bgcolor: "#fff3e0",
        label: "PROCESSING",
      };

    default:
      return {
        color: "#616161",
        bgcolor: "#f5f5f5",
        label: status.replace(/_/g, " ").toUpperCase(),
      };
  }
};

export const ProductItem = ({ product, isLastItem }: ProductItemProps) => {
  const statusConfig = product.status ? getStatusConfig(product.status) : null;

  return (
    <Box
      mb={isLastItem ? 0 : 2.5}
      pb={isLastItem ? 0 : 2.5}
      sx={{
        borderBottom: isLastItem ? "none" : "1px solid #e0e0e0",
      }}
    >
      <Box display="flex" gap={2} alignItems="flex-start">
        <Box
          sx={{
            flexShrink: 0,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {product.type === "tango" && product.image ? (
            <CardImage
              imageUrl={product.image}
              height="auto"
              width="100px"
              margin={{ xs: "auto", sm: "auto" }}
            />
          ) : product.type === "zinc" && product.image ? (
            <ProductImage
              card={{ image: product.image } as any}
              height="auto"
              width="100px"
            />
          ) : product.image ? (
            <Box
              component="img"
              src={product.image}
              alt={product.name || "Product"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                bgcolor: "#f5f5f5",
              }}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                No Image
              </Typography>
            </Box>
          )}
        </Box>

        <Box flex={1} minWidth={0}>
          {statusConfig && (
            <Chip
              label={statusConfig.label}
              size="small"
              sx={{
                bgcolor: statusConfig.bgcolor,
                color: statusConfig.color,
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
                mb: 1,
                "& .MuiChip-label": {
                  px: 1.5,
                },
              }}
            />
          )}
          <Typography
            variant="body1"
            sx={{
              color: "#212121",
              fontWeight: 500,
              fontSize: "1rem",
              lineHeight: 1.4,
              display: "-webkit-box",
              WebkitLineClamp: product.type === "zinc" ? 3 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
